import { useCallback, useEffect, useRef } from 'react';
import { FilterValue } from '../types/index';
import { decodeData, encodeFiltersToParams, decodeFiltersFromParams } from '../../../utils/encodeUtils';
import { getMappingFiltersFromURL } from '../utils';
import { isActiveFilterValue } from '../../../modules/search/searchGrid/utils/activeColumnFilters';
import Filters from '../Filters';

export const useFilterManagement = (
  defaultFilterValue: FilterValue[],
  setDefaultFilterValue: (filters: FilterValue[]) => void,
  isEvidence: boolean,
  filtersLoadedFromURL: boolean,
  setFiltersLoadedFromURL: (loaded: boolean) => void,
  gridContainerRef: React.RefObject<HTMLDivElement>,
  setURLWithFilters: (url: string) => void,
  setPendingFiltersToSave: (filters: FilterValue[] | null) => void,
  setSkip: (skip: number) => void,
) => {
  const checkIncompleteFilters = useCallback(() => {
    return null;
  }, []);

  const onFilterValueChange = useCallback(
    (value: FilterValue[]) => {
      const scrollX = gridContainerRef.current?.scrollLeft || 0;
      const valueWithPanel = value.map(filter => ({
        ...filter,
        ...(isActiveFilterValue(filter.value) ? { isEvidence } : {}),
      }));
      setDefaultFilterValue(valueWithPanel);

      const filters = valueWithPanel.filter(v => isActiveFilterValue(v.value));
      const filtersWithEvidence = filters.map(filter => ({ ...filter, isEvidence }));

      setPendingFiltersToSave(filtersWithEvidence);

      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete('f');

      const tablePrefix = isEvidence ? 'evidence' : 'summary';
      const keysToDelete: string[] = [];
      for (const key of currentUrl.searchParams.keys()) {
        if (key.startsWith(`filters-${tablePrefix}-`)) {
          keysToDelete.push(key);
        }
        if (key.startsWith(`filters[${tablePrefix}][`)) {
          keysToDelete.push(key);
        }
      }
      keysToDelete.forEach(key => currentUrl.searchParams.delete(key));

      if (filtersWithEvidence.length > 0) {
        const filterParams = encodeFiltersToParams(filtersWithEvidence);
        for (const [key, value] of filterParams.entries()) {
          currentUrl.searchParams.set(key, value);
        }
      }

      window.history.replaceState({}, '', currentUrl.href);
      setURLWithFilters(currentUrl.href);

      setTimeout(() => {
        if (gridContainerRef.current) gridContainerRef.current.scrollLeft = scrollX;
      }, 0);
    },
    [isEvidence, gridContainerRef, setDefaultFilterValue, setPendingFiltersToSave, setURLWithFilters],
  );

  const handleClearAllFilters = useCallback(() => {
    const baseFilters = Filters.filter((f: any) => f).map((f: any) => ({ ...f, value: '' }));

    setSkip(0);

    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('f');

      const tablePrefix = isEvidence ? 'evidence' : 'summary';
      const keysToDelete: string[] = [];
      for (const key of url.searchParams.keys()) {
        if (key.startsWith(`filters-${tablePrefix}-`)) {
          keysToDelete.push(key);
        }
        if (key.startsWith(`filters[${tablePrefix}][`)) {
          keysToDelete.push(key);
        }
      }
      keysToDelete.forEach(key => url.searchParams.delete(key));

      window.history.replaceState({}, '', url);
      setURLWithFilters('');
    } catch (e) {
      // ignore URL errors
    }

    setDefaultFilterValue(baseFilters);
    onFilterValueChange(baseFilters);
  }, [onFilterValueChange, isEvidence, setSkip, setURLWithFilters, setDefaultFilterValue]);

  const handleRemoveFilter = useCallback(
    (filterToRemove: FilterValue) => {
      const newFilters = defaultFilterValue.map(f => {
        if (
          f.name === filterToRemove.name &&
          (f.type !== 'number' || f.operator === filterToRemove.operator)
        ) {
          return { ...f, value: '' };
        }
        return f;
      });

      setDefaultFilterValue(newFilters);
      onFilterValueChange(newFilters);
    },
    [defaultFilterValue, onFilterValueChange, setDefaultFilterValue],
  );

  const handleRemoveFilterValue = useCallback(
    (filterName: string, valueToRemove: string) => {
      const newFilters = defaultFilterValue.map(f => {
        if (f.name !== filterName) return f;
        if (!Array.isArray(f.value)) return f;
        const next = (f.value as string[]).filter(v => String(v).trim() !== String(valueToRemove).trim());
        return { ...f, value: next.length > 0 ? next : '' };
      });
      setDefaultFilterValue(newFilters);
      onFilterValueChange(newFilters);
    },
    [defaultFilterValue, onFilterValueChange, setDefaultFilterValue],
  );

  const handleFilterChange = useCallback(
    (columnId: string, value: string) => {
      const newFilters = defaultFilterValue.map(f => {
        if (f.name === columnId) {
          // Convert comma-separated string to array for multi-select filters
          const arrayValue = value ? value.split(',').filter(Boolean) : '';
          return { ...f, value: arrayValue };
        }
        return f;
      });
      onFilterValueChange(newFilters);
    },
    [defaultFilterValue, onFilterValueChange],
  );

  const handleFilterOperatorChange = useCallback(
    (columnId: string, operator: string) => {
      const newFilters = defaultFilterValue.map(f => (f.name === columnId ? { ...f, operator } : f));
      setDefaultFilterValue(newFilters);
      onFilterValueChange(newFilters);
    },
    [defaultFilterValue, onFilterValueChange, setDefaultFilterValue],
  );

  const handleFilterBatch = useCallback(
    (mutator: (filters: FilterValue[]) => FilterValue[]) => {
      const newFilters = mutator([...defaultFilterValue]);
      onFilterValueChange(newFilters);
    },
    [defaultFilterValue, onFilterValueChange],
  );

  const lastAppliedFilterTokenRef = useRef<string | null>(null);

  // Load filters from URL on mount and when URL filter param changes (e.g. applying saved filter from profile)
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const filterToken = p.get('f');

    const filterSignature = (() => {
      const parts: string[] = [];
      if (filterToken) parts.push(`f=${filterToken}`);
      for (const [k, v] of p.entries()) {
        if (k.startsWith('filters-') || k.startsWith('filters[')) {
          parts.push(`${k}=${v}`);
        }
      }
      return parts.sort().join('&');
    })();

    const urlChanged = filterSignature !== lastAppliedFilterTokenRef.current;

    if (filtersLoadedFromURL && !urlChanged) return;

    let decodedFilters = decodeFiltersFromParams(p);

    if (decodedFilters.length === 0) {
      if (filterToken) {
        try {
          decodedFilters = decodeData(filterToken);
        } catch (error) {
          // Ignore decode errors
        }
      }
    }

    if (decodedFilters?.length) {
      // Accept filters that match current grid, or have no isEvidence (apply to current grid when only one panel is visible)
      const relevantFilters = decodedFilters.filter(
        (f: FilterValue) => f.isEvidence === undefined || f.isEvidence === isEvidence,
      );

      if (relevantFilters.length > 0) {
        const newFilters = (() => {
          const baseFiltersMap = new Map(
            Filters.filter((f: any) => f).map((f: any) => [f.name, { ...f, value: f.value || '' }]),
          );

          relevantFilters.forEach((f: FilterValue) => {
            const baseFilter = Filters.find((bf: any) => bf.name === f.name);
            if (baseFilter) {
              baseFiltersMap.set(f.name, {
                ...baseFilter,
                ...f,
                type: baseFilter.type,
                integer: baseFilter.integer,
                value: f.value || '',
              });
            } else {
              baseFiltersMap.set(f.name, { ...f, value: f.value || '' });
            }
          });

          return Array.from(baseFiltersMap.values());
        })();

        setDefaultFilterValue(newFilters);
      }
      lastAppliedFilterTokenRef.current = filterSignature;
      setFiltersLoadedFromURL(true);
    } else {
      const mappingFilters = getMappingFiltersFromURL(p);
      if (mappingFilters.length > 0) {
        const mergedByName = new Map<string, FilterValue>();
        defaultFilterValue.forEach(f => {
          if (f?.name) mergedByName.set(f.name, f);
        });
        mappingFilters.forEach(f => {
          if (f?.name) mergedByName.set(f.name, f);
        });
        setDefaultFilterValue(Array.from(mergedByName.values()));
      }
      lastAppliedFilterTokenRef.current = filterSignature;
      setFiltersLoadedFromURL(true);
    }
  }, [
    location.search,
    isEvidence,
    filtersLoadedFromURL,
    setDefaultFilterValue,
    setFiltersLoadedFromURL,
    defaultFilterValue,
  ]);

  return {
    checkIncompleteFilters,
    onFilterValueChange,
    handleClearAllFilters,
    handleRemoveFilter,
    handleRemoveFilterValue,
    handleFilterChange,
    handleFilterOperatorChange,
    handleFilterBatch,
  };
};
